import { createRouter, createWebHistory } from 'vue-router'
import Home from '../pages/Home.vue'
import Contact from '../pages/Contact.vue'
import ServicesDigital from '../pages/ServicesDigital.vue'
import ServicesFinancial from '../pages/ServicesFinancial.vue'
import ServicesCloud from '../pages/ServicesCloud.vue'
import ServicesAI from '../pages/ServicesAI.vue'
import ServicesITStaff from '../pages/ServicesITStaff.vue'
import PrivacyPolicy from '../pages/PrivacyPolicy.vue'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: {
      title: 'Opalina - Premium Financial & IT Solutions',
      description: 'Leading provider of financial technology, digital transformation, cloud solutions, and AI automation services.',
      keywords: 'fintech, digital transformation, cloud services, AI automation, IT solutions',
      og: {
        title: 'Opalina - Premium Financial & IT Solutions',
        description: 'Leading provider of financial technology, digital transformation, cloud solutions, and AI automation services.',
        type: 'website',
        image: '/assets/images/og-home.jpg'
      },
      twitter: {
        card: 'summary_large_image',
        title: 'Opalina - Premium Financial & IT Solutions',
        description: 'Leading provider of financial technology, digital transformation, cloud solutions, and AI automation services.'
      }
    }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: Contact,
    meta: {
      title: 'Contact Opalina - Get in Touch for Technology Solutions',
      description: 'Contact our experts to discuss your technology needs. Get started with premium financial and IT solutions today.',
      keywords: 'contact opalina, technology consultation, business inquiry, get quote'
    }
  },
  {
    path: '/service-digital',
    name: 'ServicesDigital',
    component: ServicesDigital,
    meta: {
      title: 'Digital Transformation Services - Modernize Your Business',
      description: 'Complete digital transformation solutions to modernize your business processes and enhance customer experience.',
      keywords: 'digital transformation, business modernization, digital solutions, process automation'
    }
  },
  {
    path: '/enterprise-banking-services',
    name: 'ServicesFinancial',
    component: ServicesFinancial,
    meta: {
      title: 'Enterprise Banking Services & Fintech Solutions - Opalina',
      description: 'Advanced financial technology solutions including payment processing, banking APIs, and fintech innovations.',
      keywords: 'enterprise banking, fintech, financial services, payment processing, banking solutions',
      og: {
        title: 'Enterprise Banking Services & Fintech Solutions',
        description: 'Advanced financial technology solutions for modern businesses.',
        type: 'article',
        image: '/assets/images/og-financial.jpg'
      }
    }
  },
  {
    path: '/service-cloud',
    name: 'ServicesCloud',
    component: ServicesCloud,
    meta: {
      title: 'Cloud Computing Solutions - Scalable & Secure',
      description: 'Enterprise-grade cloud solutions for scalability, security, and cost optimization. AWS, Azure, and Google Cloud expertise.',
      keywords: 'cloud computing, AWS, Azure, Google Cloud, cloud migration, cloud security'
    }
  },
  {
    path: '/ai-automations',
    name: 'ServicesAI',
    component: ServicesAI,
    meta: {
      title: 'AI Automation Solutions - Intelligent Business Processes',
      description: 'Cutting-edge AI automation solutions to streamline operations, reduce costs, and enhance productivity.',
      keywords: 'AI automation, artificial intelligence, process automation, machine learning, intelligent systems'
    }
  },
  {
    path: '/service-it-staff',
    name: 'ServicesITStaff',
    component: ServicesITStaff,
    meta: {
      title: 'IT Staff Augmentation - Skilled Technology Professionals',
      description: 'Access skilled IT professionals to augment your team. Flexible staffing solutions for your technology projects.',
      keywords: 'IT staff augmentation, technology consulting, skilled developers, IT professionals'
    }
  },
  {
    path: '/fintech-solution',
    name: 'FintechSolution',
    component: ServicesFinancial,
    meta: {
      title: 'Fintech Solutions - Advanced Financial Technology',
      description: 'Innovative fintech solutions for modern financial services. Payment systems, digital banking, and financial APIs.',
      keywords: 'fintech solutions, financial technology, digital banking, payment systems, financial APIs'
    }
  },
  {
    path: '/service-it-aug',
    name: 'ITAugmentation',
    component: ServicesITStaff,
    meta: {
      title: 'IT Augmentation Services - Expert Technology Teams',
      description: 'Professional IT augmentation services to scale your development teams with expert technology professionals.',
      keywords: 'IT augmentation, technology teams, software development, IT consulting, tech staffing'
    }
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicy,
    meta: {
      title: 'Privacy Policy - Opalina Technologies',
      description: 'Privacy Policy for Opalina Technologies. Learn how we collect, use, and protect your personal information.',
      keywords: 'privacy policy, data protection, personal information, cookies, GDPR',
      og: {
        title: 'Privacy Policy - Opalina Technologies',
        description: 'Learn how we collect, use, and protect your personal information.',
        type: 'website'
      }
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // If there's a hash, scroll to that element
    if (to.hash) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const element = document.querySelector(to.hash)
          if (element) {
            resolve({
              el: to.hash,
              behavior: 'smooth',
              top: 0
            })
          } else {
            resolve({ top: 0 })
          }
        }, 500) // Wait for page to load
      })
    }
    // If there's a saved position (back/forward), use it
    if (savedPosition) {
      return savedPosition
    }
    // Otherwise scroll to top
    return { top: 0 }
  }
})

// Navigation guard to update meta tags
router.beforeEach((to, from, next) => {
  // Update document title
  if (to.meta?.title) {
    document.title = to.meta.title
  }

  // Remove existing meta tags
  const existingMetas = document.querySelectorAll('meta[data-vue-router-controlled]')
  existingMetas.forEach(meta => meta.remove())

  // Add new meta tags
  if (to.meta?.description) {
    const metaDescription = document.createElement('meta')
    metaDescription.setAttribute('name', 'description')
    metaDescription.setAttribute('content', to.meta.description)
    metaDescription.setAttribute('data-vue-router-controlled', '')
    document.head.appendChild(metaDescription)
  }

  if (to.meta?.keywords) {
    const metaKeywords = document.createElement('meta')
    metaKeywords.setAttribute('name', 'keywords')
    metaKeywords.setAttribute('content', to.meta.keywords)
    metaKeywords.setAttribute('data-vue-router-controlled', '')
    document.head.appendChild(metaKeywords)
  }

  // Add Open Graph meta tags
  if (to.meta?.og) {
    Object.keys(to.meta.og).forEach(key => {
      const metaOg = document.createElement('meta')
      metaOg.setAttribute('property', `og:${key}`)
      metaOg.setAttribute('content', to.meta.og[key])
      metaOg.setAttribute('data-vue-router-controlled', '')
      document.head.appendChild(metaOg)
    })
  }

  // Add Twitter Card meta tags
  if (to.meta?.twitter) {
    Object.keys(to.meta.twitter).forEach(key => {
      const metaTwitter = document.createElement('meta')
      metaTwitter.setAttribute('name', `twitter:${key}`)
      metaTwitter.setAttribute('content', to.meta.twitter[key])
      metaTwitter.setAttribute('data-vue-router-controlled', '')
      document.head.appendChild(metaTwitter)
    })
  }

  next()
})

export default router
